import React from 'react';
import { useIntl } from 'react-intl';
import { TikTokSettingsDraft, tiktokCommercialLabel } from 'fym-shared/src/music24/tiktokPostSettings';
import { TypedMemo } from '../../../../common/types';
import { SettingsCheckRow } from '../settings/SettingsCheckRow';
import { SettingsSwitchRow } from '../settings/SettingsSwitchRow';
import { tiktokMessages } from './tiktokMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TikTokDisclosureFieldsProps {
  draft: TikTokSettingsDraft;
  onDiscloseContent: (value: boolean) => void;
  onYourBrand: (value: boolean) => void;
  onBrandedContent: (value: boolean) => void;
}

/**
 * The commercial content disclosure (TikTok Direct Post G5). The switch starts off. On, the user
 * picks "Your brand" ("Promotional content") and/or "Branded content" ("Paid partnership"); with no
 * pick the post stays blocked and a hint says why. The label line names the label TikTok shows.
 */
const TikTokDisclosureFieldsComponent: React.FC<TikTokDisclosureFieldsProps> = ({
  draft,
  onDiscloseContent,
  onYourBrand,
  onBrandedContent,
}) => {
  const intl = useIntl();
  const label = tiktokCommercialLabel(draft);
  const hasNoOption = draft.discloseContent && !draft.yourBrand && !draft.brandedContent;

  return (
    <div className={styles.group} data-testid="tiktok-disclosure">
      <SettingsSwitchRow
        id="tiktok-disclose"
        title={intl.formatMessage(tiktokMessages.discloseTitle)}
        note={intl.formatMessage(tiktokMessages.discloseNote)}
        checked={draft.discloseContent}
        onChange={onDiscloseContent}
      />
      {draft.discloseContent ? (
        <>
          <SettingsCheckRow
            id="tiktok-your-brand"
            title={intl.formatMessage(tiktokMessages.yourBrandTitle)}
            note={intl.formatMessage(tiktokMessages.yourBrandNote)}
            checked={draft.yourBrand}
            onChange={onYourBrand}
          />
          <SettingsCheckRow
            id="tiktok-branded"
            title={intl.formatMessage(tiktokMessages.brandedTitle)}
            note={intl.formatMessage(tiktokMessages.brandedNote)}
            checked={draft.brandedContent}
            onChange={onBrandedContent}
          />
          {hasNoOption ? <p className={styles.small}>{intl.formatMessage(tiktokMessages.disclosureHint)}</p> : null}
          {label === null ? null : (
            <p className={styles.small} data-testid="tiktok-label-line">
              {intl.formatMessage(tiktokMessages.labelLine)}
              <span className={styles.label}>
                {intl.formatMessage(
                  label === 'paidPartnership' ? tiktokMessages.labelPaid : tiktokMessages.labelPromotional
                )}
              </span>
            </p>
          )}
        </>
      ) : null}
    </div>
  );
};

export const TikTokDisclosureFields = TypedMemo(TikTokDisclosureFieldsComponent);
